import React from 'react';

/**
 * Kat Karşılığı bölümlerinin paylaşım durumu için ortak renk skalası ve detay kartı.
 * Vaziyet (KatCizimPanel, pinStyle="durum") ve Görünüm (KatKarsiligGorunumPaylasim) aynı görünümü kullanır.
 */

export interface BolumDetayVeri {
    bolumAdi: string;
    bolumTipi?: string | null;
    m2?: number | null;
    fiyat?: number | null;
    paylasimlar: { ad: string; tip?: 'O' | 'M'; oranYuzde: number }[];
}

const fmt = (n: number) => n.toLocaleString('tr-TR', { maximumFractionDigits: 2 });

export const toplamPaylasimOrani = (b: { paylasimlar: { oranYuzde: number }[] }) =>
    b.paylasimlar.reduce((s, p) => s + p.oranYuzde, 0);

/** Paylaşım yoksa gri; %1 → %100 arası kırmızı → sarı → yeşil. */
export const paylasimRengi = (oran: number): string => {
    if (oran <= 0) return '#94a3b8';
    if (oran >= 100) return '#16a34a';
    const ton = Math.round(Math.max(0, Math.min(100, oran)) * 1.1); // 0 kırmızı, 55 sarı, 110 yeşile yakın
    return `hsl(${ton}, 75%, 42%)`;
};

/** Haritalarda bölüm adını durum renginde gösteren küçük etiket. */
export const BolumEtiketi: React.FC<{ ad: string; oran: number; vurgulu?: boolean; hedef?: boolean; pasif?: boolean }> = ({ ad, oran, vurgulu, hedef, pasif }) => (
    <div style={{
        background: hedef ? '#cd6906' : paylasimRengi(oran),
        color: '#fff', fontSize: 11.5, fontWeight: 700, lineHeight: 1.2,
        padding: '3px 8px', borderRadius: 5, whiteSpace: 'nowrap',
        border: `2px solid ${vurgulu || hedef ? '#fff' : 'rgba(255,255,255,0.55)'}`,
        boxShadow: vurgulu || hedef ? '0 0 0 2px rgba(15,23,42,0.55), 0 4px 12px rgba(0,0,0,0.35)' : '0 2px 6px rgba(0,0,0,0.35)',
        opacity: pasif ? 0.35 : 1,
        display: 'flex', alignItems: 'center', gap: 4,
    }}>
        {oran >= 100 && <i className="fas fa-check" style={{ fontSize: 9 }}></i>}
        {ad}
    </div>
);

/** Üzerine gelince / tıklayınca açılan detay kartı. */
export const BolumDetayKarti: React.FC<{
    bolum: BolumDetayVeri;
    onKapat?: () => void;
    onPaylasimEkle?: () => void;
}> = ({ bolum, onKapat, onPaylasimEkle }) => {
    const oran = toplamPaylasimOrani(bolum);
    const renk = paylasimRengi(oran);
    return (
        <div onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}
            style={{
                width: 220, background: '#fff', borderRadius: 10, padding: '10px 12px', textAlign: 'left',
                boxShadow: '0 10px 28px rgba(15,23,42,0.32)', border: `2px solid ${renk}`, cursor: 'default',
                fontSize: 12, color: '#334155', whiteSpace: 'normal',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {oran >= 100 && <i className="fas fa-check-circle" style={{ color: '#16a34a' }} title="Paylaşım tamamlandı"></i>}
                <span style={{ fontWeight: 800, fontSize: 13.5, color: '#0f172a', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bolum.bolumAdi}</span>
                {onKapat && (
                    <button onClick={onKapat} title="Kapat"
                        style={{ border: 'none', background: '#f1f5f9', color: '#64748b', borderRadius: 6, width: 22, height: 22, cursor: 'pointer', fontSize: 11, padding: 0 }}>
                        <i className="fas fa-times"></i>
                    </button>
                )}
            </div>
            <div style={{ color: '#64748b', fontSize: 11.5, marginTop: 2 }}>
                {[bolum.bolumTipi, bolum.m2 ? `${fmt(bolum.m2)} m²` : null, bolum.fiyat ? `${fmt(bolum.fiyat)} ₺` : null].filter(Boolean).join(' · ') || '—'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '7px 0 6px' }}>
                <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, oran)}%`, height: '100%', background: renk }} />
                </div>
                <span style={{ fontWeight: 800, color: renk, fontSize: 12 }}>%{fmt(oran)}</span>
            </div>
            {bolum.paylasimlar.length === 0 ? (
                <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 11.5 }}>Paylaşım yapılmadı</div>
            ) : bolum.paylasimlar.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '2px 0', borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
                    {p.tip && (
                        <span style={{ fontSize: 9.5, fontWeight: 800, borderRadius: 3, padding: '0 4px', background: p.tip === 'O' ? '#dcfce7' : '#fef9c3', color: p.tip === 'O' ? '#15803d' : '#854d0e' }}>{p.tip}</span>
                    )}
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.ad}>{p.ad}</span>
                    <span style={{ fontWeight: 700, color: '#15803d' }}>%{fmt(p.oranYuzde)}</span>
                    {bolum.fiyat ? <span style={{ color: '#94a3b8', fontSize: 10.5 }}>{fmt(bolum.fiyat * p.oranYuzde / 100)} ₺</span> : null}
                </div>
            ))}
            {onPaylasimEkle && oran < 100 && (
                <button onClick={onPaylasimEkle}
                    style={{ marginTop: 8, width: '100%', border: 'none', background: '#fff7ed', color: '#cd6906', borderRadius: 6, fontSize: 11.5, fontWeight: 700, padding: '5px 0', cursor: 'pointer' }}>
                    <i className="fas fa-share-alt" style={{ marginRight: 5 }}></i>Paylaşım ekle
                </button>
            )}
        </div>
    );
};

/** Renk skalası lejantı. */
export const PaylasimRenkSkalasi: React.FC<{ ek?: React.ReactNode }> = ({ ek }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 11.5, color: '#64748b' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: paylasimRengi(0) }} />Paylaşılmadı
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            %1
            <span style={{ width: 110, height: 10, borderRadius: 5, background: `linear-gradient(90deg, ${paylasimRengi(1)}, ${paylasimRengi(50)}, ${paylasimRengi(99)})` }} />
            %99
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: paylasimRengi(100) }} />Tamamlandı
        </span>
        {ek && <span style={{ marginLeft: 'auto' }}>{ek}</span>}
    </div>
);
